import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, Popper, MenuItem, Typography } from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import Iconify from 'src/components/iconify/Iconify';
import Scrollbar from 'src/components/scrollbar/Scrollbar';

import { TABLE_HEAD_ADMIN } from '../tableHeads';

FilterPopover.propTypes = {
  data: PropTypes.array,
  setSubFilterPopover: PropTypes.func,
  subFilterPopover: PropTypes.object,
  handleCloseSubFilter: PropTypes.func,
  setFilterBy: PropTypes.func,
  setFilterName: PropTypes.func,
  setSearchBy: PropTypes.func,
  setPage: PropTypes.func,
  departmentsData: PropTypes.array,
  handleCloseFilter: PropTypes.func,
  filterPopover: PropTypes.object,
};
export default function FilterPopover({
  data = [], // Extracted to dynamically populate unique roles from current table list
  setSubFilterPopover,
  subFilterPopover,
  handleCloseSubFilter,
  setFilterBy,
  setFilterName,
  setSearchBy,
  setPage,
  departmentsData,
  handleCloseFilter,
  filterPopover,
}) {
  const [selectedFilter, setSelectedFilter] = useState('fullName');

  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();

  const uniqueRoles = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.map((u) => u.role).filter(Boolean))];
  }, [data]);

  const formatRoleDisplay = (role) => {
    if (!role) return '—';
    const translated = t(`roles.${role}`);
    if (translated === `roles.${role}` || translated.startsWith('roles.')) {
      return role
        .replace('ROLE_', '')
        .replace(/_/g, ' ')
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return translated;
  };

  const handleOpenSubFilter = (event, category) => {
    if (category === 'department') {
      setSubFilterPopover({ department: event.currentTarget });
    } else if (category === 'status') {
      setSubFilterPopover({ status: event.currentTarget });
    } else if (category === 'role') {
      setSubFilterPopover({ role: event.currentTarget });
    }
  };

  return (
    <>
      {/* main popper */}
      <Popper
        open={Boolean(filterPopover)}
        anchorEl={filterPopover}
        placement="left-start"
        onClose={handleCloseFilter}
      >
        <Card
          sx={{
            p: 1,
            minwidth: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
              boxShadow: '0.5px 0.5px 2 px',
            },
          }}
        >
          {TABLE_HEAD_ADMIN.map(({ id, name }) => (
            <div key={id}>
              {name === 'status' ? (
                <MenuItem
                  selected={selectedFilter === 'status'}
                  onMouseEnter={(e) => handleOpenSubFilter(e, 'status')}
                  onTouchStart={(e) => handleOpenSubFilter(e, 'status')}
                  onClick={(e) => handleOpenSubFilter(e, 'status')}
                >
                  <Iconify
                    icon="eva:arrow-ios-back-outline"
                    sx={{
                      mr: isSmallScreen ? 1.5 : 2,
                      width: isSmallScreen ? 16 : 20,
                      height: isSmallScreen ? 16 : 20,
                    }}
                  />
                  <Typography variant="body2">
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isSmallScreen && '0.7rem',
                      }}
                    >
                      {t('filters.byStatus')}
                    </Typography>
                  </Typography>
                </MenuItem>
              ) : null}
              {name === 'appUserRole' ? (
                <MenuItem
                  selected={selectedFilter === 'role'}
                  onMouseEnter={(e) => handleOpenSubFilter(e, 'role')}
                  onTouchStart={(e) => handleOpenSubFilter(e, 'role')}
                  onClick={(e) => handleOpenSubFilter(e, 'role')}
                >
                  <Iconify
                    icon="eva:arrow-ios-back-outline"
                    sx={{
                      mr: isSmallScreen ? 1.5 : 2,
                      width: isSmallScreen ? 16 : 20,
                      height: isSmallScreen ? 16 : 20,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: isSmallScreen && '0.7rem',
                    }}
                  >
                    {t('filters.byRole') || 'By Role'}
                  </Typography>
                </MenuItem>
              ) : null}
              {name === 'departmentName' ? (
                <MenuItem
                  selected={selectedFilter === 'department'}
                  onMouseEnter={(e) => handleOpenSubFilter(e, 'department')}
                  onTouchStart={(e) => handleOpenSubFilter(e, 'department')}
                  onClick={(e) => handleOpenSubFilter(e, 'department')}
                >
                  <Iconify
                    icon="eva:arrow-ios-back-outline"
                    sx={{
                      mr: isSmallScreen ? 1.5 : 2,
                      width: isSmallScreen ? 16 : 20,
                      height: isSmallScreen ? 16 : 20,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: isSmallScreen && '0.7rem',
                    }}
                  >
                    {t('filters.byDepartment')}
                  </Typography>
                </MenuItem>
              ) : null}
            </div>
          ))}
        </Card>
      </Popper>
      {/* sub poppers */}
      <Popper
        open={Boolean(subFilterPopover.status)}
        anchorEl={subFilterPopover.status}
        onClose={handleCloseSubFilter}
        placement="left-start"
      >
        <Card
          sx={{
            p: 1,
            minwidth: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
              boxShadow: '0.5px 0.5px 2 px',
            },
          }}
        >
          {['true', 'false'].map((status, index) => (
            <MenuItem
              key={index}
              onClick={() => {
                setFilterBy('status');
                setSearchBy('byStatus');
                setSelectedFilter('status');
                setFilterName(status);
                handleCloseFilter();
                handleCloseSubFilter();
                setPage(0);
              }}
              onTouchStart={() => {
                setFilterBy('status');
                setSearchBy('byStatus');
                setSelectedFilter('status');
                setFilterName(status);
                handleCloseFilter();
                handleCloseSubFilter();
                setPage(0);
              }}
            >
              {status === 'true' ? (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: isSmallScreen && '0.7rem',
                  }}
                >
                  {t('userStatus.active')}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: isSmallScreen && '0.7rem',
                  }}
                >
                  {t('userStatus.inactive')}
                </Typography>
              )}
            </MenuItem>
          ))}
        </Card>
      </Popper>

      <Popper
        open={Boolean(subFilterPopover.department)}
        anchorEl={subFilterPopover.department}
        placement="left-start"
        onClose={handleCloseSubFilter}
      >
        <Card
          sx={{
            p: 1,
            minwidth: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
              boxShadow: '0.5px 0.5px 2 px',
            },
          }}
        >
          <Scrollbar sx={{ maxHeight: 200, overflowY: 'auto' }}>
            {departmentsData.map((department) => (
              <MenuItem
                key={department.id}
                onClick={() => {
                  setFilterBy('departmentName');
                  setSearchBy('byDepartment');
                  setSelectedFilter('department');
                  setFilterName(department.name);
                  handleCloseFilter();
                  handleCloseSubFilter();
                  setPage(0);
                }}
                onTouchStart={() => {
                  setFilterBy('departmentName');
                  setSearchBy('byDepartment');
                  setSelectedFilter('department');
                  setFilterName(department.name);
                  handleCloseFilter();
                  handleCloseSubFilter();
                  setPage(0);
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: isSmallScreen && '0.7rem',
                  }}
                >
                  {department.name}
                </Typography>
              </MenuItem>
            ))}
          </Scrollbar>
        </Card>
      </Popper>

      {/* Role Sub-Popper */}
      <Popper
        open={Boolean(subFilterPopover.role)}
        anchorEl={subFilterPopover.role}
        placement="left-start"
        onClose={handleCloseSubFilter}
      >
        <Card
          sx={{
            p: 1,
            minwidth: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
              boxShadow: '0.5px 0.5px 2 px',
            },
          }}
        >
          <Scrollbar sx={{ maxHeight: 200, overflowY: 'auto' }}>
            {uniqueRoles.map((roleVal) => (
              <MenuItem
                key={roleVal}
                onClick={() => {
                  setFilterBy('role');
                  setSearchBy('byRole');
                  setSelectedFilter('role');
                  setFilterName(roleVal);
                  handleCloseFilter();
                  handleCloseSubFilter();
                  setPage(0);
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: isSmallScreen && '0.7rem',
                  }}
                >
                  {formatRoleDisplay(roleVal)}
                </Typography>
              </MenuItem>
            ))}
          </Scrollbar>
        </Card>
      </Popper>
    </>
  );
}
